import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { ChevronDown, ChevronRight, Loader2, ExternalLink } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useToast } from "@/hooks/use-toast";
import { apiRequest } from "@/lib/queryClient";
import { useLocation } from "wouter";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface RctiLineItem {
  id: string;
  paymentId: string;
  accountNumber: string | null;
  dealReference: string | null;
  commissionType: string | null;
  commissionAmount: string;
  gstAmount: string;
  totalAmount: string;
}

interface PartnerPayment {
  id: string;
  partnerId: string;
  paymentMonth: number | null;
  paymentYear: number | null;
  internalReference: string | null;
  totalLineItems: number | null;
  totalCommission: string | null;
  totalGst: string | null;
  totalPaymentAmount: string | null;
  paymentStatus: string;
  paymentDate: string | null;
  paymentReceivedDate: string | null;
  xeroRctiNumber: string | null;
  rctiSentDate: string | null;
}

interface PartnerRctiPanelProps {
  partnerId: string;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const STATUS_COLORS: Record<string, string> = {
  pending: "bg-amber-500/10 text-amber-700 dark:text-amber-400",
  approved: "bg-blue-500/10 text-blue-700 dark:text-blue-400",
  processing: "bg-purple-500/10 text-purple-700 dark:text-purple-400",
  completed: "bg-green-500/10 text-green-700 dark:text-green-400",
  cancelled: "bg-gray-500/10 text-gray-700 dark:text-gray-400",
  failed: "bg-red-500/10 text-red-700 dark:text-red-400",
};

function formatCurrency(value: string | null | undefined): string {
  if (!value) return "—";
  const num = parseFloat(value);
  if (isNaN(num)) return "—";
  return new Intl.NumberFormat("en-AU", {
    style: "currency",
    currency: "AUD",
    minimumFractionDigits: 2,
  }).format(num);
}

function formatPeriod(month: number | null, year: number | null): string {
  if (!month || !year) return "—";
  const date = new Date(year, month - 1, 1);
  return date.toLocaleDateString("en-AU", { month: "long", year: "numeric" });
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function PartnerRctiPanel({ partnerId }: PartnerRctiPanelProps) {
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());
  const [lineItemsCache, setLineItemsCache] = useState<Record<string, RctiLineItem[]>>({});
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [, navigate] = useLocation();

  const { data: payments = [], isLoading } = useQuery<PartnerPayment[]>({
    queryKey: ["/api/commissions/partners", partnerId, "payments"],
    enabled: !!partnerId,
  });

  const markPaidMutation = useMutation({
    mutationFn: async (paymentId: string) => {
      const today = new Date().toISOString().split("T")[0];
      await apiRequest("PATCH", `/api/commissions/rcti/${paymentId}`, {
        paymentStatus: "completed",
        paymentReceivedDate: today,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/commissions/partners"] });
      queryClient.invalidateQueries({ queryKey: ["/api/commissions/rcti"] });
      toast({ title: "Marked as paid", description: "RCTI payment status updated to completed" });
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: error.message || "Failed to update payment status",
        variant: "destructive",
      });
    },
  });

  const toggleExpand = async (paymentId: string) => {
    const next = new Set(expandedIds);
    if (next.has(paymentId)) {
      next.delete(paymentId);
    } else {
      next.add(paymentId);
      // Fetch line items if not yet cached
      if (!lineItemsCache[paymentId]) {
        try {
          const res = await fetch(
            `/api/commissions/rcti/${paymentId}/line-items`,
            {
              headers: { "Content-Type": "application/json" },
              credentials: "include",
            }
          );
          if (res.ok) {
            const items: RctiLineItem[] = await res.json();
            setLineItemsCache((prev) => ({ ...prev, [paymentId]: items }));
          }
        } catch {
          // Silently fail — line items will show empty
        }
      }
    }
    setExpandedIds(next);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div data-testid="panel-partner-rcti">
      {/* Header */}
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-xs font-semibold text-muted-foreground">
          RCTI RECORDS ({payments.length})
        </h3>
        <Button
          size="sm"
          variant="outline"
          onClick={() => navigate("/commission/rcti")}
          data-testid="btn-generate-rcti"
        >
          <ExternalLink className="h-3 w-3 mr-1" />
          Generate RCTI
        </Button>
      </div>

      {payments.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-10 text-center border rounded-md">
          <p className="text-sm text-muted-foreground">No RCTI records for this partner</p>
          <p className="text-xs text-muted-foreground mt-1">
            Generate RCTIs from the RCTI Management page
          </p>
        </div>
      ) : (
        <div className="border rounded-md">
          <Table>
            <TableHeader>
              <TableRow className="h-10 bg-muted/50">
                <TableHead className="text-xs font-medium px-2 py-1 w-6"></TableHead>
                <TableHead className="text-xs font-medium px-2 py-1">Period</TableHead>
                <TableHead className="text-xs font-medium px-2 py-1">Internal Ref</TableHead>
                <TableHead className="text-xs font-medium px-2 py-1 text-center">Line Items</TableHead>
                <TableHead className="text-xs font-medium px-2 py-1 text-right">Total</TableHead>
                <TableHead className="text-xs font-medium px-2 py-1">Status</TableHead>
                <TableHead className="text-xs font-medium px-2 py-1">Xero RCTI #</TableHead>
                <TableHead className="text-xs font-medium px-2 py-1">Sent Date</TableHead>
                <TableHead className="text-xs font-medium px-2 py-1">Paid Date</TableHead>
                <TableHead className="text-xs font-medium px-2 py-1 w-[100px]">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {payments.map((payment) => {
                const isExpanded = expandedIds.has(payment.id);
                const lineItems = lineItemsCache[payment.id] ?? [];

                return (
                  <>
                    <TableRow
                      key={payment.id}
                      className="h-10"
                      data-testid={`row-rcti-${payment.id}`}
                    >
                      <TableCell className="px-2 py-1">
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-5 w-5"
                          onClick={() => toggleExpand(payment.id)}
                          aria-label={isExpanded ? "Collapse" : "Expand"}
                          data-testid={`btn-expand-rcti-${payment.id}`}
                        >
                          {isExpanded ? (
                            <ChevronDown className="h-3 w-3" />
                          ) : (
                            <ChevronRight className="h-3 w-3" />
                          )}
                        </Button>
                      </TableCell>
                      <TableCell className="px-2 py-1 text-sm">
                        {formatPeriod(payment.paymentMonth, payment.paymentYear)}
                      </TableCell>
                      <TableCell className="px-2 py-1 text-sm font-mono text-xs">
                        {payment.internalReference ?? "—"}
                      </TableCell>
                      <TableCell className="px-2 py-1 text-sm text-center">
                        {payment.totalLineItems ?? 0}
                      </TableCell>
                      <TableCell className="px-2 py-1 text-sm text-right">
                        {formatCurrency(payment.totalPaymentAmount)}
                      </TableCell>
                      <TableCell className="px-2 py-1">
                        <Badge
                          variant="secondary"
                          className={`text-xs capitalize ${STATUS_COLORS[payment.paymentStatus] ?? ""}`}
                          data-testid={`rcti-status-badge-${payment.paymentStatus}`}
                        >
                          {payment.paymentStatus}
                        </Badge>
                      </TableCell>
                      <TableCell className="px-2 py-1 text-sm">
                        {payment.xeroRctiNumber ?? "—"}
                      </TableCell>
                      <TableCell className="px-2 py-1 text-sm">
                        {payment.rctiSentDate ?? "—"}
                      </TableCell>
                      <TableCell className="px-2 py-1 text-sm">
                        {payment.paymentReceivedDate ?? "—"}
                      </TableCell>
                      <TableCell className="px-2 py-1">
                        <div className="flex items-center gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-7 text-xs"
                            onClick={() => navigate(`/commission/rcti`)}
                            data-testid={`btn-view-rcti-${payment.id}`}
                          >
                            View
                          </Button>
                          {payment.paymentStatus !== "completed" && (
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-7 text-xs text-green-700"
                              onClick={() => markPaidMutation.mutate(payment.id)}
                              disabled={markPaidMutation.isPending}
                              data-testid={`btn-mark-paid-${payment.id}`}
                            >
                              Mark Paid
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>

                    {/* Expanded line items */}
                    {isExpanded && (
                      <TableRow
                        key={`${payment.id}-expanded`}
                        data-testid={`expanded-rcti-${payment.id}`}
                      >
                        <TableCell colSpan={10} className="px-4 py-2 bg-muted/30">
                          {lineItems.length === 0 ? (
                            <p className="text-xs text-muted-foreground py-2">No line items</p>
                          ) : (
                            <Table>
                              <TableHeader>
                                <TableRow className="h-8">
                                  <TableHead className="text-xs font-medium px-2 py-1">Account #</TableHead>
                                  <TableHead className="text-xs font-medium px-2 py-1">Deal Ref</TableHead>
                                  <TableHead className="text-xs font-medium px-2 py-1">Type</TableHead>
                                  <TableHead className="text-xs font-medium px-2 py-1 text-right">Commission</TableHead>
                                  <TableHead className="text-xs font-medium px-2 py-1 text-right">GST</TableHead>
                                  <TableHead className="text-xs font-medium px-2 py-1 text-right">Total</TableHead>
                                </TableRow>
                              </TableHeader>
                              <TableBody>
                                {lineItems.map((item) => (
                                  <TableRow key={item.id} className="h-8">
                                    <TableCell className="px-2 py-1 text-xs">{item.accountNumber ?? "—"}</TableCell>
                                    <TableCell className="px-2 py-1 text-xs">{item.dealReference ?? "—"}</TableCell>
                                    <TableCell className="px-2 py-1 text-xs capitalize">{item.commissionType ?? "—"}</TableCell>
                                    <TableCell className="px-2 py-1 text-xs text-right">{formatCurrency(item.commissionAmount)}</TableCell>
                                    <TableCell className="px-2 py-1 text-xs text-right">{formatCurrency(item.gstAmount)}</TableCell>
                                    <TableCell className="px-2 py-1 text-xs text-right">{formatCurrency(item.totalAmount)}</TableCell>
                                  </TableRow>
                                ))}
                              </TableBody>
                            </Table>
                          )}
                        </TableCell>
                      </TableRow>
                    )}
                  </>
                );
              })}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
}
